// Smartify Message Box — mobile. Super Admin only.
//
// The desktop console puts three panes side by side. A phone has room for one,
// so the same data is behind tabs: All, Filtered (the watch list), Send.
//
// EVERYTHING BEHIND IT IS SHARED with the desktop screen — core/mqtt/mqttMonitor
// holds the buffer and the wildcard subscription, core/mqtt/consoleSubs holds
// the watch list, core/devices/commands holds the vocabulary. This file is
// layout and nothing else, which is why the two cannot drift apart.
//
// The monitor is REF-COUNTED, so opening this while the desktop pane is also
// mounted shares one subscription rather than doubling it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Tabs, Tab, TextField, Button, Typography, Chip, IconButton, useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { CenterTitleBar } from '../components/MobileAppBar';
import { useApp } from '../../../core/state/AppContext';
import {
  startMonitor, stopMonitor, subscribeMessages, logLocal, directionGlyph, watchedLines, displayText,
} from '../../../core/mqtt/mqttMonitor';
import { publishMessage, isOfflineNotice } from '../../../core/mqtt/mqttClient';
import { loadSubs, onSubsChange, addSub, removeSub } from '../../../core/mqtt/consoleSubs';
import {
  PRESET_COMMANDS, DEBUG_COMMANDS, toDeviceTopic,
} from '../../../core/devices/commands';
import { getDevicesByUser } from '../../../core/api/api';
import {
  primeLabels, getLabel, requestLabel, resolveLabel, subscribeLabels, logLineMatches,
} from '../../../core/devices/deviceLabels';
import { copyText } from '../../../core/utils/clipboard';
// A desktop component on a mobile screen — deliberately. The mobile shell
// already routes /device-admin, which renders this same card, so it is proven
// at this size; a second implementation here is the thing to avoid.
import DeviceControlCard from '../../desktop/components/DeviceControlCard';
import useConsoleDevice from '../../shared/useConsoleDevice';
import DeviceTypePicker from '../../shared/DeviceTypePicker';
import DeviceNameField from '../../shared/DeviceNameField';
import DeviceFirmwarePicker from '../../shared/DeviceFirmwarePicker';
import FirmwareFacts from '../../shared/FirmwareFacts';
import UnassignDeviceButton from '../../shared/UnassignDeviceButton';
import AssignDeviceButton from '../../shared/AssignDeviceButton';
// "I think this is installed at …" for every UNASSIGNED device the log hears.
import UnassignedHintBar from '../../shared/UnassignedHintBar';
import RfScanButton from '../../shared/RfScanButton';
import RfLineMenu from '../../shared/RfLineMenu';
import useRfDirectory from '../../../core/hooks/useRfDirectory';
import { parseRfV } from '../../../core/devices/rfCodes';
import { rfMatches, rfUnderLine } from '../../../core/devices/rfIdentify';
import { deviceCardWidth, deviceTypeCaption } from '../../../core/devices/deviceMeta';

// Dense monospace: the point of the screen is scanning a stream, so more lines
// beats comfortable text — the same reasoning as the desktop pane.
const MONO = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 10, lineHeight: 1.45 };

export default function MqttConsoleScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const { userId, isSuperAdmin } = useApp();

  const [tab, setTab] = useState(0);
  const [messages, setMessages] = useState([]);
  const [watched, setWatched] = useState([]);
  const [subs, setSubs] = useState([]);
  const [search, setSearch] = useState('');
  const [topic, setTopic] = useState('');
  // Resolved once the operator pauses typing, and auto-watched — see the hook.
  const {
    device: consoleDevice, reload: reloadConsoleDevice, refreshedAt: consoleReadAt,
  } = useConsoleDevice(topic);
  const [text, setText] = useState('');
  const [subInput, setSubInput] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Subs first: the monitor files a line into its watched ring by them.
    setSubs(loadSubs());
    startMonitor();
    const off = subscribeMessages((buf, kept) => {
      setMessages([...buf]);
      setWatched([...kept]);
    });
    const offSubs = onSubsChange((s) => setSubs([...s]));
    return () => { off(); offSubs(); stopMonitor(); };
  }, []);

  // uno -> "Owner - Loc/Room/Name". The SAME resolver as the desktop console
  // (core/devices/deviceLabels): the own-device list is a free prime, and every
  // number in the log — including our own, which the list names without an
  // owner — is upgraded by one detail lookup. This screen watches '#', so the
  // owner is what tells one customer's "Home/Parking" from another's.
  useEffect(() => {
    if (!userId) return;
    getDevicesByUser(userId)
      .then((res) => primeLabels(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [userId]);

  // Repaint when a background lookup lands; the labels live in the resolver.
  const [labelTick, setLabelTick] = useState(0);
  useEffect(() => subscribeLabels(() => setLabelTick((n) => n + 1)), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const labelFor = useCallback((uno) => getLabel(uno) || '', [labelTick]);

  useEffect(() => {
    messages.forEach((m) => { if (m.uno && m.uno !== 'app') requestLabel(m.uno); });
  }, [messages]);

  // The Send box's caption — resolved directly, not through the queue.
  const [breadcrumb, setBreadcrumb] = useState('');
  useEffect(() => {
    const uno = topic.trim();
    if (!uno) { setBreadcrumb(''); return undefined; }
    const known = getLabel(uno);
    if (known !== null) { setBreadcrumb(known || 'Unknown device'); return undefined; }
    let alive = true;
    setBreadcrumb('…');
    resolveLabel(uno).then((l) => { if (alive) setBreadcrumb(l || 'Unknown device'); });
    return () => { alive = false; };
  }, [topic, labelTick]);

  // Per-tab clear floors — see clearShown below.
  const [allFloor, setAllFloor] = useState(0);
  const [filteredFloor, setFilteredFloor] = useState(0);

  const filtered = useMemo(() => {
    const unos = new Set(subs.map((s) => s.uniqueNumber));
    return watchedLines(messages, watched, unos, filteredFloor);
  }, [messages, watched, subs, filteredFloor]);

  /*
   * ⚠️ THE ONE TRASH BUTTON CLEARS WHICHEVER TAB IS OPEN, AND ONLY THAT. It
   * used to call `clearMessages`, which empties the store both tabs are views
   * of — so clearing on Filtered wiped All. Each tab keeps the message id it
   * was cleared at instead; the store is untouched. Same fix as the desktop
   * console's two CLEAR buttons.
   */
  const clearShown = () => {
    const top = messages[0] ? messages[0].id : 0; // newest first
    if (tab === 1) setFilteredFloor(top); else setAllFloor(top);
  };

  const allShown = useMemo(
    () => messages.filter((m) => m.id > allFloor),
    [messages, allFloor],
  );

  // An RfV line names the SENSOR — RF number, name, type, meaning, room
  // (core/devices/rfIdentify). Estate-wide: this screen is Super Admin only.
  const { describe: rfDescribe, ready: rfReady, refreshIfStale: rfRefresh } = useRfDirectory({ all: !!isSuperAdmin });
  const rfFor = useCallback((text) => {
    const hit = parseRfV(text);
    return hit && rfReady ? rfDescribe(hit.code) : null;
  }, [rfDescribe, rfReady]);
  const [rfMenu, setRfMenu] = useState(null);   // { rf, receiverUno, x, y }

  const shown = useMemo(() => {
    const list = tab === 1 ? filtered : allShown;
    const q = search.trim().toLowerCase();
    // Search also finds an RF sensor by its RF number, name or hw id.
    return q ? list.filter((m) => logLineMatches(m.text, m.uno, q) || rfMatches(rfFor(m.text), q)) : list;
  // labelTick: a caption resolving can make a line match (logLineMatches reads it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtered, allShown, search, rfFor, labelTick]);

  // A code we cannot place means the list is old (throttled in the hook).
  const newestRf = messages.find((m) => parseRfV(m.text));
  const newestRfStatus = newestRf ? rfFor(newestRf.text)?.status : '';
  useEffect(() => {
    if (newestRfStatus === 'new') rfRefresh();
  }, [newestRf && newestRf.id, newestRfStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Raw, with no [uno]…#userId envelope — this is a diagnostic tool and the
  // point is to send exactly what was typed. Same as the desktop pane.
  const send = (message) => {
    const t = topic.trim();
    if (!t || !message) return;
    const full = toDeviceTopic(t);
    publishMessage(full, message);
    logLocal(`➡️ published "${message}" to ${full}`);
  };

  const doCopy = async () => {
    const ok = await copyText(shown.map((m) => {
      const label = labelFor(m.uno);
      const rf = rfFor(m.text);
      const line = `${m.time} ${directionGlyph(m.dir)} ${displayText(m)}${label ? ` ${label}` : ''}`;
      if (rf) return `${line}\n      ↳ ${rfUnderLine(rf)}`;
      return line;
    }).join('\n'));
    setCopied(ok);
    setTimeout(() => setCopied(false), 1600);
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back }}>
        <CenterTitleBar title="Message Box" showBack />
        <Typography sx={{ color: c.text, p: 3, textAlign: 'center' }}>
          This screen is for administrators.
        </Typography>
      </Box>
    );
  }

  const list = (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1 }}>
      {shown.length === 0 ? (
        <Typography sx={{ color: `${c.text}99`, fontSize: 12, p: 2, textAlign: 'center' }}>
          {tab === 1 ? 'Add a unique number to watch it.' : 'Waiting for traffic…'}
        </Typography>
      ) : shown.map((m) => {
        const label = labelFor(m.uno);
        const rf = rfFor(m.text);
        return (
          <Box key={m.id} sx={{ ...MONO, color: c.text, py: '2px', borderBottom: `1px solid ${c.text}22`, wordBreak: 'break-all' }}>
            <Box component="span" sx={{ opacity: 0.6, mr: 0.5 }}>{m.time}</Box>
            {/* Direction first — a command and its echo read identically otherwise. */}
            <Box component="span" sx={{ display: 'inline-block', width: '1.1em', fontWeight: 700, mr: 0.25, color: m.dir === 'out' ? '#FFB74D' : m.dir === 'in' ? '#81C784' : `${c.text}99` }}>{directionGlyph(m.dir)}</Box>
            {/* The FULL payload is always shown, an RfV too (owner, 2026-10-14); the
                sensor it names goes on a second line. Tap (or long-press) that line:
                set the sensor up (RfLineMenu). */}
            {displayText(m)}
            {label && (
              <Box component="span" sx={{ color: '#81C784', ml: 0.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {label}
              </Box>
            )}
            {rf && (
              <Box
                onClick={(e) => setRfMenu({ rf, receiverUno: m.uno, x: e.clientX, y: e.clientY })}
                onContextMenu={(e) => { e.preventDefault(); setRfMenu({ rf, receiverUno: m.uno, x: e.clientX, y: e.clientY }); }}
                sx={{
                  pl: 1.5, wordBreak: 'normal', fontWeight: 700, cursor: 'pointer',
                  color: rf.status === 'named' ? '#64B5F6' : '#FFB74D',
                }}
              >
                ↳ {rfUnderLine(rf)}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar
        title="Message Box"
        showBack
        actions={<RfScanButton iconOnly sx={{ color: c.text }} />}
      />

      <Tabs
        value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 12, color: `${c.text}99` },
          '& .Mui-selected': { color: `${c.text} !important` } }}
      >
        {/* Counts what the tab SHOWS, not the store — or the badge would still
            read 240 after the tab was cleared. */}
        <Tab label={`All (${allShown.length})`} />
        <Tab label={`Filtered (${filtered.length})`} />
        <Tab label="Send" />
      </Tabs>

      {/* The reminder about every unassigned device heard — where the network
          says it lives, and Assign with the owner pre-picked (siteGuess). On
          the log tabs only: the Send tab is for typing, not for reading. */}
      {tab !== 2 && (
        <Box sx={{ px: 1, pt: 0.5 }}>
          <UnassignedHintBar messages={messages} onPick={(uno) => { setTopic(uno); setTab(2); }} max={2} />
        </Box>
      )}

      {tab !== 2 && (
        <>
          <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, alignItems: 'center' }}>
            <TextField
              size="small" fullWidth placeholder="Search…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ style: { fontSize: 12, padding: '5px 8px', color: c.text } }}
            />
            <IconButton size="small" onClick={doCopy} disabled={shown.length === 0} sx={{ color: copied ? '#81C784' : c.text }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={clearShown} sx={{ color: c.text }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          {tab === 1 && (
            <Box sx={{ px: 1, pb: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {subs.map((s) => (
                <Chip
                  key={s.uniqueNumber} size="small" label={s.uniqueNumber}
                  onDelete={() => removeSub(s.uniqueNumber)}
                  deleteIcon={<CloseIcon />}
                  sx={{ height: 20, fontSize: 10, color: c.text, borderColor: `${c.text}55` }}
                  variant="outlined"
                />
              ))}
              <TextField
                size="small" placeholder="Unique number" value={subInput}
                onChange={(e) => setSubInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !subInput.trim()) return;
                  addSub(subInput.trim());
                  setSubInput('');
                }}
                inputProps={{ style: { fontSize: 11, padding: '4px 8px', color: c.text } }}
                sx={{ width: 130 }}
              />
              <IconButton
                size="small" sx={{ color: c.text }}
                onClick={() => { if (subInput.trim()) { addSub(subInput.trim()); setSubInput(''); } }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {list}
        </>
      )}

      {tab === 2 && (
        <Box sx={{ p: 1.5, overflowY: 'auto' }}>
          <TextField
            size="small" fullWidth label="Unique number" value={topic}
            onChange={(e) => setTopic(e.target.value)}
            sx={{ '& .MuiInputBase-input': { color: c.text }, '& .MuiInputLabel-root': { color: `${c.text}99` } }}
          />
          <Typography sx={{ color: `${c.text}99`, fontSize: 11, mt: 0.5 }}>
            {breadcrumb || ' '}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {[...PRESET_COMMANDS, ...DEBUG_COMMANDS].map((cmd) => (
              <Button
                key={cmd} size="small" variant="outlined"
                onClick={() => send(cmd)} disabled={!topic.trim()}
                sx={{ minWidth: 0, fontSize: 11, py: 0.1, color: c.text, borderColor: `${c.text}55` }}
              >
                {cmd}
              </Button>
            ))}
          </Box>

          {/* The device's REAL controls, chosen by DeviceID through the same
              DeviceControlCard the room grid, /device-admin and the desktop
              console use — see the long note in the desktop console. The
              mobile shell already renders this card through /device-admin, so
              this is the same component on the same screen size it already
              serves. ⚠️ It publishes for real. */}
          {consoleDevice && (
            <Box sx={{ mt: 1.5 }}>
              {/* Type + DeviceID, not just the installer's name — see desktop. */}
              <Typography sx={{ color: `${c.text}99`, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                Controls · {deviceTypeCaption(consoleDevice)}
              </Typography>
              {/* Same editor as the desktop console: on site, with the device
                  in your hand, is exactly where a wrong type is found. */}
              <DeviceTypePicker device={consoleDevice} onSaved={reloadConsoleDevice} dense />
              <DeviceNameField device={consoleDevice} onSaved={reloadConsoleDevice} dense />
              <DeviceFirmwarePicker device={consoleDevice} onSaved={reloadConsoleDevice} dense />
              {/* When it last updated vs the newest file it would download. */}
              <FirmwareFacts
                device={consoleDevice} refreshedAt={consoleReadAt} onRefresh={reloadConsoleDevice}
                // The newest message FROM this device in the log — it is alive then,
                // whatever the server's last status change says (2026-10-17).
                heardAt={messages.find((m) => m.dir === 'in' && !isOfflineNotice(m.payload) && String(m.uno) === String(consoleDevice?.UniqueNumber))?.at?.getTime?.() ?? null}
              />
              {/* Back to stock, password-guarded — the same action as Device Admin. */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {Number(consoleDevice.LocationID ?? consoleDevice.LocationId) === 0 && (
                  <AssignDeviceButton uniqueNumber={consoleDevice.UniqueNumber} onDone={reloadConsoleDevice} sx={{ mt: 0.25, mb: 0.5 }} />
                )}
                <UnassignDeviceButton uniqueNumber={consoleDevice.UniqueNumber} device={consoleDevice} onDone={reloadConsoleDevice} sx={{ mt: 0.25, mb: 0.5 }} />
              </Box>
              {/* Sized by the panel, as the desktop strip does — a one-button
                  card at full phone width is one very wide button. */}
              <Box sx={{ width: deviceCardWidth(consoleDevice.DeviceID), maxWidth: '100%' }}>
                <DeviceControlCard device={consoleDevice} subtitle={deviceTypeCaption(consoleDevice)} bench />
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
            <TextField
              size="small" fullWidth placeholder="Custom message" value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send(text)}
              inputProps={{ style: { color: c.text } }}
            />
            <Button
              variant="contained" onClick={() => send(text)}
              disabled={!topic.trim() || !text.trim()}
              sx={{ bgcolor: c.button, minWidth: 44 }}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Box>
        </Box>
      )}
      <RfLineMenu target={rfMenu} onClose={() => setRfMenu(null)} />
    </Box>
  );
}
